import NextLink from 'next/link';

import { Button, ButtonProps } from '@playbooks/ui/components/button';
import { Spinner } from '@playbooks/ui/components/spinner';
import { cn } from '@playbooks/ui/lib/utils';

type LinkProps = Omit<ButtonProps, 'disabled' | 'isLoading' | 'render'> & {
	href: string;
	target?: string;
	rel?: string;
	disabled?: boolean;
	isPending?: boolean;
	loadingLabel?: string;
};

const Link = ({
	href,
	target,
	rel,
	disabled = false,
	isPending = false,
	loadingLabel = 'Loading',
	className,
	children,
	onClick,
	...props
}: LinkProps) => {
	const hasDestination = Boolean(href);
	const isDisabled = disabled || isPending || !hasDestination;
	const resolvedRel = rel || (target === '_blank' ? 'noopener noreferrer' : undefined);
	const renderElement = hasDestination ? <NextLink href={href} target={target} rel={resolvedRel} /> : <span />;

	// Theme
	const rootClassName = cn(isDisabled && 'pointer-events-none opacity-50', className);

	// Render
	return (
		<Button
			nativeButton={false}
			render={renderElement}
			aria-busy={isPending || undefined}
			aria-disabled={isDisabled || undefined}
			aria-label={isPending ? loadingLabel : props['aria-label']}
			tabIndex={isDisabled ? -1 : props.tabIndex}
			className={rootClassName}
			onClick={event => {
				if (isDisabled) {
					event.preventDefault();
					return;
				}
				onClick?.(event);
			}}
			{...props}>
			{isPending ? (
				<>
					<span aria-hidden className='invisible flex items-center gap-[inherit]'>
						{children}
					</span>
					<span aria-hidden className='absolute inset-0 flex items-center justify-center'>
						<Spinner />
					</span>
				</>
			) : (
				children
			)}
		</Button>
	);
};

export { Link };
export type { LinkProps };
